/**
 * ListItem — Icon/thumbnail + title + subtitle + right element
 *
 * Used for: settings rows, store lists, menu items
 */
import type { ReactNode } from 'react'
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'

const ListItemFrame = styled(XStack, {
  name: 'ListItem',
  tag: 'button',
  cursor: 'pointer',
  userSelect: 'none',
  ai: 'center',
  gap: '$sm',
  py: '$sm',
  px: '$md',
  br: '$lg',
  animation: 'quick',
  animateOnly: ['opacity', 'transform', 'background-color'],
  pressStyle: { bg: '$backgroundSecondary', scale: 0.98 },
  hoverStyle: { bg: '$backgroundSecondary' },
})

const ListItemContent = styled(YStack, {
  name: 'ListItemContent',
  f: 1,
  gap: 2,
})

const ListItemTitle = styled(TamaguiText, {
  name: 'ListItemTitle',
  fontFamily: '$body',
  fontSize: 15,
  fontWeight: '500',
  color: '$color',
})

const ListItemSubtitle = styled(TamaguiText, {
  name: 'ListItemSubtitle',
  fontFamily: '$body',
  fontSize: 13,
  color: '$colorSecondary',
})

type ListItemFrameProps = GetProps<typeof ListItemFrame>

interface ListItemProps extends ListItemFrameProps {
  icon?: ReactNode
  title: string
  subtitle?: string
  right?: ReactNode
}

export const ListItem = ListItemFrame.styleable<ListItemProps>(
  ({ icon, title, subtitle, right, onPress, ...props }, ref) => {
    return (
      <ListItemFrame ref={ref} onPress={onPress} {...props}>
        {icon}
        <ListItemContent>
          <ListItemTitle>{title}</ListItemTitle>
          {subtitle && <ListItemSubtitle>{subtitle}</ListItemSubtitle>}
        </ListItemContent>
        {right}
      </ListItemFrame>
    )
  },
)
